import {GridColumns, GridRenderCellParams} from '@mui/x-data-grid';
import {FormattedMessage} from 'react-intl';
import {ColorField} from 'src/components/common/ColorField';
import {TableIdCell} from 'src/components/common/TableIdCell';
import {ContractSortFieldEnum} from 'src/enums';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';

/**
 * Takes what the caller may see rather than what they are called. The role name it replaced
 * could not answer for a business-defined role, and the column would simply have stayed hidden.
 */
export const columnConfigContracts = (
    isPlatformAdmin: boolean,
    isCompany: boolean
): GridColumns => [
    {
        field: ContractSortFieldEnum.CONTRACT_NAME,
        renderHeader: (_params) => <FormattedMessage id={'nameColumnHeader'}/>,
        flex: 3,
        minWidth: 100,
        cellClassName: 'ampTableCell',
        renderCell: (params) => {
            return (
                <TableIdCell
                    businessId={params.row.business.id}
                    label={params.row.name}
                    link={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.contracts, params.row.id])}
                />
            );
        },
    },
    {
        field: ContractSortFieldEnum.CLIENT_LAST_NAME,
        renderHeader: (_params) => <FormattedMessage id={'clientColumnHeader'}/>,
        flex: 3,
        minWidth: 150,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => {
            if (params.row.user?.company?.name) {
                return params.row.user.company.name;
            }

            return `${params.row.user.firstName} ${params.row.user.lastName}`;
        },
    },
    {
        field: ContractSortFieldEnum.CLIENT_EMAIL,
        renderHeader: (_params) => <FormattedMessage id={'emailColumnHeader'}/>,
        flex: 6,
        minWidth: 210,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => params.row.user.emailAddress,
    },
    {
        field: ContractSortFieldEnum.CREATED,
        renderHeader: (_params) => <FormattedMessage id={'createdOnColumnHeader'}/>,
        flex: 3,
        minWidth: 120,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => new Date(params.row.created).toLocaleDateString('fr-CH'),
    },
    {
        field: ContractSortFieldEnum.STATUS,
        renderHeader: (_params) => <FormattedMessage id={'statusColumnHeader'}/>,
        flex: 3,
        minWidth: 110,
        cellClassName: 'ampTableCell',
        renderCell: (params) => {
            return <ColorField label={params.row.status}/>;
        },
    },
    ...(isCompany
        ? []
        : [
            {
                field: ContractSortFieldEnum.LOCATION_NAME,
                renderHeader: (_params: unknown) => <FormattedMessage id={'locationColumnHeader'}/>,
                flex: 7,
                minWidth: 160,
                cellClassName: 'ampTableCell',
                renderCell: (params: GridRenderCellParams) =>
                    params.row.location && (
                        <TableIdCell
                            businessId={params.row.business.id}
                            label={params.row.location?.name}
                            link={AppRouteSpace([
                                AppRouteEnum.app,
                                AppRouteEnum.locations,
                                params.row.location?.id,
                            ])}
                            withoutCopy
                        />
                    ),
            },
        ]),
    {
        field: 'business',
        renderHeader: (_params) => <FormattedMessage id={'businessColumnHeader'}/>,
        flex: 2,
        minWidth: 130,
        sortable: false,
        // Hidden for everybody now. It was shown only to a platform administrator, back when
        // their lists spanned every tenant; a list that is one business repeats that business's
        // name down the whole column instead.
        hide: true,
        cellClassName: 'ampTableCell',
        valueGetter: (params) => params.row.business.name,
    },
];
